import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";
import { RagStatus } from "apps/project-iq/components/WorkspacePlan/WorkspacePlan.types";
import {
  dashboardTokens,
  PillToneKey,
} from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.tokens";

const ragDotToneStyle = (hex: string) => ({
  backgroundColor: hex,
  boxShadow: `0 0 0 1px ${hex}`,
});

export const useGroupedWorkspaceDashboardStyles = makeStyles({
  pageBody: {
    flex: 1,
    overflowY: "auto",
    backgroundColor: projectIqTokens.bg,
  },
  pageContent: {
    maxWidth: dashboardTokens.page.maxWidth,
    margin: "0 auto",
    padding: dashboardTokens.page.padding,
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
    width: "100%",
  },

  groupList: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },
  groupCard: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: dashboardTokens.card.radius,
    boxShadow: projectIqTokens.shadowSm,
    overflow: "hidden",
  },
  groupHeaderButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `13px ${dashboardTokens.indent.portfolio}`,
    backgroundColor: projectIqTokens.card,
    border: "none",
    cursor: "pointer",
    textAlign: "start",
    fontFamily: projectIqTokens.fontFamily,
    ":hover": {
      backgroundColor: projectIqTokens.bg,
    },
  },
  chevron: {
    color: projectIqTokens.faint,
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    transition: `transform ${dashboardTokens.chevronTransition}`,
  },
  chevronExpanded: {
    transform: "rotate(90deg)",
  },
  groupName: {
    fontSize: "15px",
    fontWeight: 800,
    color: projectIqTokens.ink,
    flex: 1,
  },
  groupCountPill: {
    fontSize: "12px",
    fontWeight: 700,
    color: projectIqTokens.teal,
    backgroundColor: projectIqTokens.tealSoft,
    borderRadius: designTokens.borderRadius.full,
    padding: "4px 12px",
    whiteSpace: "nowrap",
  },
  groupBody: {
    borderTop: `1px solid ${projectIqTokens.line}`,
  },

  workspaceGrid: {
    display: "grid",
    gridTemplateColumns: dashboardTokens.grid.workspaceRow,
    gap: "4px",
    alignItems: "center",
  },
  workspaceHeaderRow: {
    padding: `9px 14px 9px ${dashboardTokens.indent.workspace}`,
    backgroundColor: projectIqTokens.bg,
    borderBottom: `1px solid ${projectIqTokens.line2}`,
  },
  workspaceColumnHeader: {
    fontSize: "11.5px",
    fontWeight: 700,
    color: projectIqTokens.faint,
  },
  workspaceColumnHeaderCentered: {
    textAlign: "center",
  },
  workspaceRow: {
    padding: `12px 14px 12px ${dashboardTokens.indent.workspace}`,
    backgroundColor: projectIqTokens.card,
    borderBottom: `1px solid ${projectIqTokens.line2}`,
  },
  workspaceRowExpanded: {
    borderBottom: "none",
  },
  workspaceToggleCell: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    minWidth: 0,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    textAlign: "start",
    fontFamily: projectIqTokens.fontFamily,
  },
  workspaceNameGroup: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: "1 1 auto",
    gap: "2px",
  },
  workspaceName: {
    fontSize: "13.5px",
    fontWeight: 700,
    color: projectIqTokens.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  workspaceClient: {
    fontSize: "11.5px",
    fontWeight: 400,
    color: projectIqTokens.faint,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  ragCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
  },
  ragDot: {
    width: dashboardTokens.ragDot.size,
    height: dashboardTokens.ragDot.size,
    minWidth: dashboardTokens.ragDot.size,
    borderRadius: designTokens.borderRadius.full,
    border: `2px solid ${projectIqTokens.card}`,
    flexShrink: 0,
  },
  ragDotGreen: ragDotToneStyle(dashboardTokens.ragDot.tone.green),
  ragDotAmber: ragDotToneStyle(dashboardTokens.ragDot.tone.amber),
  ragDotRed: ragDotToneStyle(dashboardTokens.ragDot.tone.red),
  countCell: {
    textAlign: "center",
  },
  countValue: {
    fontSize: "15px",
    fontWeight: 800,
    color: projectIqTokens.ink,
  },
  countValueZero: {
    color: projectIqTokens.ink3,
  },

  detailRow: {
    backgroundColor: projectIqTokens.bg,
    padding: `14px 18px 18px ${dashboardTokens.indent.detail}`,
  },
  tabStripWrapper: {
    borderBottom: `1px solid ${projectIqTokens.line}`,
    marginBottom: designTokens.spacing[3],
  },
  tabList: {
    display: "flex",
    gap: "22px",
  },
  logTab: {
    display: "inline-flex",
    padding: "0 0 10px 0",
    margin: 0,
    backgroundColor: "transparent",
    border: "none",
    borderRadius: 0,
    borderBottom: "2px solid transparent",
    fontSize: "13px",
    fontWeight: 700,
    fontFamily: projectIqTokens.fontFamily,
    color: projectIqTokens.muted,
    cursor: "pointer",
    transition: `border-color ${dashboardTokens.chevronTransition}, color ${dashboardTokens.chevronTransition}`,
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },
  logTabActive: {
    color: projectIqTokens.teal,
    borderBottom: `2px solid ${projectIqTokens.teal}`,
  },
  tabPanel: {
    marginTop: designTokens.spacing[3],
  },

  logPanelCard: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: dashboardTokens.card.radius,
    boxShadow: projectIqTokens.shadowSm,
    overflow: "hidden",
  },
  logPanelHeaderRow: {
    display: "grid",
    gap: "12px",
    padding: "8px 14px",
    backgroundColor: projectIqTokens.bg,
    borderBottom: `1px solid ${projectIqTokens.line2}`,
  },
  logPanelColumnHeader: {
    fontSize: "11px",
    fontWeight: 700,
    color: projectIqTokens.faint,
    textAlign: "start",
  },
  logPanelDataRow: {
    display: "grid",
    gap: "12px",
    padding: "11px 14px",
    borderBottom: `1px solid ${projectIqTokens.line2}`,
  },
  logPanelDataRowLast: {
    borderBottom: "none",
  },
  logPanelCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    minWidth: 0,
    textAlign: "start",
    fontSize: "13px",
    fontWeight: 600,
    color: projectIqTokens.ink,
  },
  logPanelCellMuted: {
    fontSize: "12.5px",
    fontWeight: 400,
    color: projectIqTokens.muted,
  },
  logPanelNameMilestone: {
    fontSize: "13.5px",
    fontWeight: 700,
    color: projectIqTokens.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  logPanelNameDeliverable: {
    fontSize: "13.5px",
    fontWeight: 600,
    color: projectIqTokens.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  logPanelNameRaid: {
    fontSize: "13px",
    fontWeight: 600,
    color: projectIqTokens.ink,
    lineHeight: dashboardTokens.raidNameLineHeight,
  },

  pill: {
    fontSize: dashboardTokens.pill.fontSize,
    fontWeight: 700,
    borderRadius: designTokens.borderRadius.full,
    padding: dashboardTokens.pill.padding,
    whiteSpace: "nowrap",
  },
  pillGreen: {
    backgroundColor: dashboardTokens.pillPair.green.background,
    color: dashboardTokens.pillPair.green.color,
  },
  pillBlue: {
    backgroundColor: dashboardTokens.pillPair.blue.background,
    color: dashboardTokens.pillPair.blue.color,
  },
  pillNeutral: {
    backgroundColor: dashboardTokens.pillPair.neutral.background,
    color: dashboardTokens.pillPair.neutral.color,
  },
  pillRed: {
    backgroundColor: dashboardTokens.pillPair.red.background,
    color: dashboardTokens.pillPair.red.color,
  },
  pillOrange: {
    backgroundColor: dashboardTokens.pillPair.orange.background,
    color: dashboardTokens.pillPair.orange.color,
  },
  pillTypeNeutral: {
    backgroundColor: dashboardTokens.pillPair.typeNeutral.background,
    color: dashboardTokens.pillPair.typeNeutral.color,
  },

  logEmptyStateCard: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: dashboardTokens.card.radius,
    boxShadow: projectIqTokens.shadowSm,
    padding: "22px 14px",
    textAlign: "center",
  },
  logEmptyStateHeadline: {
    fontSize: "13px",
    fontWeight: 700,
    color: projectIqTokens.muted,
    margin: 0,
  },
  logEmptyStateHint: {
    fontSize: "12px",
    fontWeight: 400,
    color: projectIqTokens.faint,
    marginTop: "3px",
  },
  noWorkspacesMessage: {
    padding: "22px 14px",
    textAlign: "center",
    fontSize: "13px",
    color: projectIqTokens.muted,
  },

  skeletonRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `13px ${dashboardTokens.indent.portfolio}`,
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: dashboardTokens.card.radius,
    boxShadow: projectIqTokens.shadowSm,
  },
  skeletonChevron: {
    width: "13px",
    height: "13px",
    flexShrink: 0,
  },
  skeletonName: {
    height: "15px",
    flex: 1,
    maxWidth: "220px",
    borderRadius: designTokens.borderRadius.sm,
  },
  skeletonPill: {
    height: "20px",
    width: "96px",
    flexShrink: 0,
    borderRadius: designTokens.borderRadius.full,
  },
  visuallyHidden: {
    position: "absolute",
    width: "1px",
    height: "1px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    clip: "rect(0,0,0,0)",
  },
});

type GroupedWorkspaceDashboardStyles = ReturnType<
  typeof useGroupedWorkspaceDashboardStyles
>;

export const getPillToneClassName = (
  styles: GroupedWorkspaceDashboardStyles,
  toneKey: PillToneKey | undefined,
): string => {
  switch (toneKey) {
    case "green":
      return styles.pillGreen;
    case "blue":
      return styles.pillBlue;
    case "neutral":
      return styles.pillNeutral;
    case "red":
      return styles.pillRed;
    case "orange":
      return styles.pillOrange;
    case "typeNeutral":
      return styles.pillTypeNeutral;
    default:
      return styles.pillNeutral;
  }
};

export const getRagDotClassName = (
  styles: GroupedWorkspaceDashboardStyles,
  status: RagStatus,
): string => {
  switch (status) {
    case "green":
      return styles.ragDotGreen;
    case "amber":
      return styles.ragDotAmber;
    case "red":
      return styles.ragDotRed;
  }
};
